import { useTranslation } from 'react-i18next'
import { extractYouTubeId } from '@quebon/shared-learning'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { SolutionVideoPlayer } from '@/components/special-lecture/SolutionVideoPlayer'
import { CoachingVideoPlayer } from './component-details/parts/CoachingVideoPlayer'

export interface UnitAssessmentSolutionVideo {
  itemNo: number
  playbackUrl: string
  originalUrl: string
}

interface Props {
  video: UnitAssessmentSolutionVideo | null
  onClose: () => void
}

/** 교사용 미리보기 전용. 학습자 videoWatched 상태는 변경하지 않는다. */
export function UnitAssessmentSolutionVideoDialog({ video, onClose }: Props) {
  const { t } = useTranslation('coaching')
  const isYouTubeVideo = extractYouTubeId(video?.playbackUrl) != null

  return (
    <Dialog open={video != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-3xl"
        overlayClassName="bg-black/40 supports-backdrop-filter:backdrop-blur-none"
        showCloseButton={false}
      >
        {video ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {t('unitAssessment.solutionVideo.dialogTitle', { no: video.itemNo })}
              </DialogTitle>
              <DialogDescription>
                {t('unitAssessment.solutionVideo.dialogDescription')}
              </DialogDescription>
            </DialogHeader>

            {isYouTubeVideo ? (
              <SolutionVideoPlayer
                videoUrl={video.playbackUrl}
                title={t('unitAssessment.solutionVideo.iframeTitle', { no: video.itemNo })}
                playLabel={t('unitAssessment.solutionVideo.play')}
                pauseLabel={t('unitAssessment.solutionVideo.pause')}
                progressLabel={t('unitAssessment.solutionVideo.progress')}
                rewindLabel={t('unitAssessment.solutionVideo.rewind')}
                seekPolicyLabel={t('unitAssessment.solutionVideo.unrestrictedSeek')}
                restrictSeeking={false}
              />
            ) : (
              <CoachingVideoPlayer videoUrl={video.playbackUrl} />
            )}

            <p className="text-xs leading-5 text-muted-foreground">
              {t('unitAssessment.solutionVideo.playbackFallback')}{' '}
              <a
                className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                href={video.originalUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t('unitAssessment.solutionVideo.openOriginal')}
              </a>
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('unitAssessment.solutionVideo.close')}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
